import { ROUTES } from '@/shared/constants/routes';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/useStore';
import { useTrans } from '@/shared/hooks/useTranslation';
import { useProjectSwitcher } from '@/viewmodel/features/project/hooks/useProjectSwitcher';
import {
  clearCurrentProject,
  selectCurrentProject,
  setCurrentProject,
} from '@/viewmodel/features/project/state/projectSlice';
import type { ScopeCrumbs } from '@/viewmodel/features/project/types';
import {
  selectCurrentWorkspace,
  setCurrentWorkspace,
  useWorkspaceQuery,
} from '@/viewmodel/features/workspace';
import type { BreadcrumbSwitcherConfig } from '@/viewmodel/shared/breadcrumbs';
import { useNavigate } from 'react-router-dom';

export function useScopeCrumbs(): ScopeCrumbs {
  const { t } = useTrans();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentWorkspace = useAppSelector(selectCurrentWorkspace);
  const currentProject = useAppSelector(selectCurrentProject);
  const { workspaces } = useWorkspaceQuery();

  const workspaceSwitcher: BreadcrumbSwitcherConfig = {
    options: workspaces.map((ws) => ({ value: ws.id ?? '', label: ws.name ?? '' })),
    currentValue: currentWorkspace?.id ?? '',
    onSelect: (id) => {
      const ws = workspaces.find((w) => w.id === id);
      if (ws) {
        dispatch(setCurrentWorkspace(ws));
        dispatch(clearCurrentProject());
        navigate(ROUTES.PROJECTS);
      }
    },
    filterPlaceholder: t('workspaces.filterPlaceholder'),
  };

  const projectSwitcher = useProjectSwitcher({
    workspaceId: currentWorkspace?.id,
    currentProjectId: currentProject?.id,
    onSelect: (proj) => {
      if (proj) {
        dispatch(setCurrentProject(proj));
        navigate(ROUTES.DATASET);
      }
    },
  });

  return {
    workspace: (href) => ({
      label: currentWorkspace?.name ?? t('workspaces.title'),
      href,
      switcher: workspaceSwitcher,
    }),
    project: {
      label: currentProject?.slug ?? t('projects.title'),
      href: ROUTES.DATASET,
      switcher: projectSwitcher,
    },
  };
}
